import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { Card, Page } from '../../app/layout/Page';
import { queryKeys } from '../../app/query/queryClient';
import { catalogApi } from '../catalog/api';
import type { CatalogApi } from '../catalog/api';
import type { PumpTemplateListItem } from '../catalog/types';
import { fleetApi } from './api';
import { curveLibraryApi, type CurveLibraryApi } from '../curve_import/libraryApi';
import type {
  FleetApi,
  FleetPumpReference,
  FleetUnitInput,
  FleetUnitRead,
} from './types';

import './fleet.css';
import { guided } from '../../app/guidance/fieldHelpRegistryCore';
import { fleetFieldHelp } from '../../app/guidance/fieldHelpRegistry';

interface FleetWorkspaceProps {
  api?: FleetApi;
  catalog?: Pick<CatalogApi, 'getPump' | 'listPumps'>;
  library?: Pick<CurveLibraryApi, 'getEntry'>;
  reviewedTarget?: {
    entryId: string;
    templateId: string;
    revisionId: string;
    variantId: string;
  } | null;
}

interface FleetFormState {
  engineMake: string;
  availability: FleetUnitInput['availability'];
  engineMaxRpm: string;
  engineMinRpm: string;
  engineModel: string;
  enginePowerHp: string;
  engineSerial: string;
  gearRatio: string;
  name: string;
  physicalIdentifier: string;
  pumpMaxRpm: string;
  pumpMinRpm: string;
  pumpRpmManual: boolean;
  revisionId: string;
  speedIncrementRpm: string;
  templateId: string;
  variantId: string;
}

const WATTS_PER_MECHANICAL_HP = 745.6998715822702;

function horsepowerToWatts(value: string): number {
  return Number(value) * WATTS_PER_MECHANICAL_HP;
}

function wattsToHorsepower(value: number): string {
  return (value / WATTS_PER_MECHANICAL_HP).toFixed(3).replace(/\.?0+$/u, '');
}

interface CatalogVariantOption {
  curveEligible: boolean;
  id: string;
  name: string;
}

function catalogVariants(value: unknown): CatalogVariantOption[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((candidate: unknown) => {
    if (
      typeof candidate !== 'object' ||
      candidate === null ||
      !('id' in candidate) ||
      !('name' in candidate) ||
      typeof candidate.id !== 'string' ||
      typeof candidate.name !== 'string'
    ) {
      return [];
    }
    const curveEligible =
      'has_optimization_eligible_curve' in candidate &&
      candidate.has_optimization_eligible_curve === true;
    return [{ curveEligible, id: candidate.id, name: candidate.name }];
  });
}

function templateLabel(template: Omit<PumpTemplateListItem, 'updated_at'>): string {
  const state = template.verification_state.replaceAll('_', ' ');
  const eligibility = template.has_optimization_eligible_curve
    ? 'curve ready'
    : 'curve needed';
  const archive = template.archived ? ' · archived' : '';
  return `${template.template_name} · ${template.manufacturer} ${template.model} · ${template.revision_label} · ${state} · ${eligibility}${archive}`;
}

const emptyForm: FleetFormState = {
  availability: 'available',
  engineMake: '',
  engineMaxRpm: '2200',
  engineMinRpm: '1000',
  engineModel: '',
  enginePowerHp: '536.408',
  engineSerial: '',
  gearRatio: '1',
  name: '',
  physicalIdentifier: '',
  pumpMaxRpm: '2200',
  pumpMinRpm: '1000',
  pumpRpmManual: false,
  revisionId: '',
  speedIncrementRpm: '25',
  templateId: '',
  variantId: '',
};
function coherentDriverPowerW(
  source: FleetUnitRead | null,
  engineRatedPowerW: number,
): number {
  if (source === null) return engineRatedPowerW;
  const defaultToleranceW = Math.max(1, source.engine_rated_power_w * 1.0e-9);
  const followedOldNameplate =
    Math.abs(
      source.maximum_driver_power_w - source.engine_rated_power_w,
    ) <= defaultToleranceW;
  if (followedOldNameplate) return engineRatedPowerW;
  return Math.min(source.maximum_driver_power_w, engineRatedPowerW);
}


function unitPayload(
  form: FleetFormState,
  source: FleetUnitRead | null,
): FleetUnitInput {
  const engineRatedPowerW = horsepowerToWatts(form.enginePowerHp);
  return {
    availability: form.availability,
    current_location: source?.current_location ?? null,
    derate_fraction: source?.derate_fraction ?? 1,
    engine_make: form.engineMake,
    engine_model: form.engineModel,
    engine_rated_power_w: engineRatedPowerW,
    engine_serial: form.engineSerial || null,
    gear_ratio: Number(form.gearRatio),
    home_location: source?.home_location ?? null,
    maximum_driver_power_w: coherentDriverPowerW(source, engineRatedPowerW),
    maximum_engine_speed_rpm: Number(form.engineMaxRpm),
    maximum_pump_speed_rpm: Number(form.pumpMaxRpm),
    minimum_engine_speed_rpm: Number(form.engineMinRpm),
    minimum_pump_speed_rpm: Number(form.pumpMinRpm),
    name: form.name,
    override: null,
    physical_identifier: form.physicalIdentifier,
    pump_template_id: form.templateId,
    pump_template_revision_id: form.revisionId,
    pump_variant_id: form.variantId,
    speed_increment_rpm: Number(form.speedIncrementRpm),
  };
}

function calculatedPumpRpm(engineRpm: string, gearRatio: string): string {
  if (engineRpm.trim() === '' || gearRatio.trim() === '') return '';
  const engine = Number(engineRpm);
  const ratio = Number(gearRatio);
  if (!Number.isFinite(engine) || !Number.isFinite(ratio)) return '';
  return String(Math.round(engine * ratio));
}

function withCalculatedPumpRpm(form: FleetFormState): FleetFormState {
  return {
    ...form,
    pumpMaxRpm: calculatedPumpRpm(form.engineMaxRpm, form.gearRatio),
    pumpMinRpm: calculatedPumpRpm(form.engineMinRpm, form.gearRatio),
    pumpRpmManual: false,
  };
}

function hasManualPumpRpm(unit: FleetUnitRead): boolean {
  return (
    String(unit.minimum_pump_speed_rpm) !==
      calculatedPumpRpm(
        String(unit.minimum_engine_speed_rpm),
        String(unit.gear_ratio),
      ) ||
    String(unit.maximum_pump_speed_rpm) !==
      calculatedPumpRpm(
        String(unit.maximum_engine_speed_rpm),
        String(unit.gear_ratio),
      )
  );
}

function editForm(unit: FleetUnitRead): FleetFormState {
  return {
    availability: unit.availability,
    engineMake: unit.engine_make,
    engineMaxRpm: String(unit.maximum_engine_speed_rpm),
    engineMinRpm: String(unit.minimum_engine_speed_rpm),
    engineModel: unit.engine_model,
    enginePowerHp: wattsToHorsepower(unit.engine_rated_power_w),
    engineSerial: unit.engine_serial ?? '',
    gearRatio: String(unit.gear_ratio),
    name: unit.name,
    physicalIdentifier: unit.physical_identifier,
    pumpMaxRpm: String(unit.maximum_pump_speed_rpm),
    pumpMinRpm: String(unit.minimum_pump_speed_rpm),
    pumpRpmManual: hasManualPumpRpm(unit),
    revisionId: unit.pump.revision_id,
    speedIncrementRpm: String(unit.speed_increment_rpm),
    templateId: unit.pump.template_id,
    variantId: unit.pump.variant_id,
  };
}

export function FleetWorkspace({ api = fleetApi, catalog = catalogApi, library = curveLibraryApi, reviewedTarget = null }: FleetWorkspaceProps) {
  const queryClient = useQueryClient();
  const keys = queryKeys.resource('fleet-units');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [notice, setNotice] = useState('');
  const [editingPump, setEditingPump] = useState<FleetPumpReference | null>(null);
  const [editingUnit, setEditingUnit] = useState<FleetUnitRead | null>(null);
  const reviewedSeedHandled = useRef<string | null>(null);
  const archived = showArchived ? 'include' : 'exclude';

  const templates = useQuery({
    queryFn: () => catalog.listPumps('', 'include'),
    queryKey: ['flow-wizard', 'fleet-pump-templates'],
  });
  const reviewedSeed = useQuery({
    enabled: reviewedTarget !== null,
    queryKey: ['curve-library', 'fleet-seed', reviewedTarget],
    queryFn: async () => {
      if (reviewedTarget === null) throw new Error('No reviewed pump was selected.');
      const [detail, pump] = await Promise.all([
        library.getEntry(reviewedTarget.entryId), catalog.getPump(reviewedTarget.templateId),
      ]);
      const valid = detail.entry.review_state === 'approved' && !pump.archived &&
        pump.current_revision.id === reviewedTarget.revisionId &&
        pump.current_revision.verification_state === 'verified' &&
        catalogVariants(pump.current_revision.variants).some((variant) => variant.id === reviewedTarget.variantId) &&
        detail.entry.revision_ids.length > 0 && detail.entry.revision_ids.every((revisionId) =>
          detail.catalog_targets.some((target) => target.active && target.curve_revision_id === revisionId &&
            target.pump_template_id === reviewedTarget.templateId &&
            target.pump_template_revision_id === reviewedTarget.revisionId &&
            target.pump_variant_id === reviewedTarget.variantId));
      return { valid, pump, label: `${detail.entry.manufacturer} ${detail.entry.model}` };
    },
    retry: false,
  });
  const templateOptions: Array<Omit<PumpTemplateListItem, 'updated_at'>> = [...(templates.data?.items ?? [])];
  if (reviewedSeed.data?.valid && !templateOptions.some((item) => item.id === reviewedSeed.data.pump.id)) {
    const pump = reviewedSeed.data.pump;
    templateOptions.push({
      id: pump.id, archived: pump.archived, template_name: pump.template_name,
      manufacturer: pump.current_revision.manufacturer, model: pump.current_revision.model,
      revision_id: pump.current_revision.id, revision_label: pump.current_revision.revision_label,
      revision_number: pump.current_revision.revision_number,
      verification_state: pump.current_revision.verification_state, has_optimization_eligible_curve: true,
    });
  }
  const selectedTemplate = templateOptions.find(
    (candidate) => candidate.id === form.templateId,
  );
  useEffect(() => {
    if (reviewedTarget === null || reviewedSeed.data === undefined) return;
    const key = `${reviewedTarget.entryId}:${reviewedTarget.templateId}:${reviewedTarget.revisionId}:${reviewedTarget.variantId}`;
    if (reviewedSeedHandled.current === key) return;
    reviewedSeedHandled.current = key;
    if (!reviewedSeed.data.valid || editingId !== null) return;
    setForm((current) => current.templateId !== '' ? current : {
      ...current, templateId: reviewedTarget.templateId,
      revisionId: reviewedTarget.revisionId, variantId: reviewedTarget.variantId,
    });
  }, [reviewedTarget, reviewedSeed.data, editingId]);
  const selectedPump = useQuery({
    enabled: form.templateId !== '',
    queryFn: () => catalog.getPump(form.templateId),
    queryKey: ['flow-wizard', 'fleet-pump-template', form.templateId],
  });
  const currentVariants = catalogVariants(selectedPump.data?.current_revision.variants);
  const variantOptions =
    editingPump !== null &&
    editingPump.variant_id === form.variantId &&
    !currentVariants.some((variant) => variant.id === editingPump.variant_id)
      ? [
          ...currentVariants,
          {
            curveEligible: editingPump.curve_eligible ?? false,
            id: editingPump.variant_id,
            name: `${editingPump.variant_name ?? 'Previously assigned variant'} (historical revision)`,
          },
        ]
      : currentVariants;

  const units = useQuery({
    queryFn: () => api.listUnits(search, archived),
    queryKey: keys.list({ archived, search }),
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.all });
  };
  const saveUnit = useMutation({
    mutationFn: (payload: FleetUnitInput) =>
      editingId === null
        ? api.createUnit(payload)
        : api.updateUnit(editingId, payload),
    onMutate: () => setNotice(''),
    onSuccess: async () => {
      setNotice(editingId === null ? 'Pump unit added.' : 'Pump unit saved.');
      setEditingId(null);
      setEditingPump(null);
      setEditingUnit(null);
      setForm(emptyForm);
      await refresh();
    },
  });
  const setArchive = useMutation({
    mutationFn: ({ archived: isArchived, id }: { archived: boolean; id: string }) =>
      isArchived ? api.restoreUnit(id) : api.archiveUnit(id),
    onSuccess: refresh,
  });
  const importUnits = useMutation({
    mutationFn: () => api.importUnits(csvText, false),
    onSuccess: async (result) => {
      setNotice(
        result.invalid_rows === 0
          ? `${result.imported_count} units imported.`
          : `No units imported. Fix ${result.invalid_rows} rows.`,
      );
      await refresh();
    },
  });

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft);
  };
  const submitUnit = (event: FormEvent) => {
    event.preventDefault();
    saveUnit.mutate(unitPayload(form, editingUnit));
  };
  const beginEdit = async (id: string) => {
    const unit = await api.getUnit(id);
    setEditingId(unit.id);
    saveUnit.reset();
    setEditingPump(unit.pump);
    setEditingUnit(unit);
    setForm(editForm(unit));
  };

  return (
    <Page title="Fleet">
      <div className="fleet-workspace">
      {reviewedTarget === null ? null : <section aria-label="Reviewed curve equipment setup">
        <h2>Use a reviewed pump configuration</h2>
        {reviewedSeed.isFetching ? <p role="status">Checking the reviewed curve and current equipment link…</p> : null}
        {reviewedSeed.isError || reviewedSeed.data?.valid === false ? <p role="alert">This reviewed equipment link could not be validated or is no longer active. No pump assignment was made from it; choose a current setup manually.</p> : null}
        {reviewedSeed.data?.valid ? <p>{reviewedSeed.data.label} is available from the reviewed library. The form uses the exact approved pump revision and variant unless you choose another setup. Enter the physical unit and driver details, then explicitly add the unit. No unit or master curve has been changed.</p> : null}
      </section>}
      <form className="fleet-toolbar" role="search" onSubmit={submitSearch}>
        <label>Search units<input {...guided(fleetFieldHelp.search)} value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} /></label>
        <button type="submit">Search</button>
        <label><input {...guided(fleetFieldHelp.showArchived)} type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />Show archived</label>
      </form>
      {notice ? <p role="status">{notice}</p> : null}
        <div className="fleet-grid">
        <div className="fleet-form-column">
          <Card title={editingId === null ? 'Add pump unit' : 'Edit pump unit'}>
            <form className="fleet-form" onSubmit={submitUnit}>
              <fieldset className="fleet-form__section">
                <legend>Identity</legend>
              <label>Unit ID<input {...guided(fleetFieldHelp.physicalId)} required value={form.physicalIdentifier} onChange={(event) => setForm({ ...form, physicalIdentifier: event.target.value })} /></label>
              <label>Name<input {...guided(fleetFieldHelp.name)} required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
              {editingUnit === null ? null : (
                <label>
                  Availability
                  <select
                    {...guided(fleetFieldHelp.availability)}
                    aria-label="Base fleet availability"
                    value={form.availability}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        availability: event.target.value as FleetUnitInput['availability'],
                      })
                    }
                  >
                    <option value="available">Available</option>
                    <option value="unavailable">Unavailable</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                  </select>
                </label>
              )}
              </fieldset>
              <fieldset className="fleet-form__section">
                <legend>Pump assignment</legend>
              <label>
                Pump model
                <select {...guided(fleetFieldHelp.pumpTemplate)}
                  required
                  value={form.templateId}
                  onChange={(event) => {
                    const template = templateOptions.find(
                      (candidate) => candidate.id === event.target.value,
                    );
                    setEditingPump(null);
                    setForm({
                      ...form,
                      revisionId: template?.revision_id ?? '',
                      templateId: event.target.value,
                      variantId: '',
                    });
                  }}
                >
                  <option value="">Choose pump model</option>
                  {templateOptions.map((template) => (
                    <option
                      disabled={template.archived}
                      key={template.id}
                      value={template.id}
                    >
                      {templateLabel(template)}
                    </option>
                  ))}
                  {editingPump !== null && selectedTemplate === undefined ? (
                    <option value={editingPump.template_id}>
                      {editingPump.template_name} — previously assigned catalog pump
                    </option>
                  ) : null}
                </select>
              </label>
              <label>
                Pump revision
                <select {...guided(fleetFieldHelp.pumpRevision)}
                  required
                  disabled={form.templateId === ''}
                  value={form.revisionId}
                  onChange={(event) =>
                    setForm({ ...form, revisionId: event.target.value, variantId: '' })
                  }
                >
                  <option value="">Choose revision</option>
                  {selectedTemplate === undefined ? null : (
                    <option value={selectedTemplate.revision_id}>
                      {selectedTemplate.revision_label} · Revision {selectedTemplate.revision_number} ·{' '}
                      {selectedTemplate.verification_state.replaceAll('_', ' ')} ·{' '}
                      {selectedTemplate.has_optimization_eligible_curve
                        ? 'curve ready'
                        : 'curve needed'}
                    </option>
                  )}
                  {editingPump !== null &&
                  editingPump.revision_id === form.revisionId &&
                  editingPump.revision_id !== selectedTemplate?.revision_id ? (
                    <option value={editingPump.revision_id}>
                      {editingPump.revision_label ?? 'Previously assigned'} · Historical revision{' '}
                      {editingPump.revision_number ?? ''}
                    </option>
                  ) : null}
                </select>
              </label>
              <label>
                Pump variant
                <select {...guided(fleetFieldHelp.pumpVariant)}
                  required
                  disabled={form.revisionId === '' || selectedPump.isLoading}
                  value={form.variantId}
                  onChange={(event) => setForm({ ...form, variantId: event.target.value })}
                >
                  <option value="">Choose variant</option>
                  {variantOptions.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name} ·{' '}
                      {variant.curveEligible
                        ? 'curve ready'
                        : 'curve needed'}
                    </option>
                  ))}
                </select>
              </label>
              {templates.isLoading ? <p role="status">Loading pump catalog…</p> : null}
              {templates.isError || selectedPump.isError ? (
                <p role="alert">Pump catalog choices could not be loaded.</p>
              ) : null}
              {selectedTemplate?.archived === true ? (
                <p role="alert">Restore this pump in Catalog before assigning it.</p>
              ) : null}
              </fieldset>
              <fieldset className="fleet-form__section">
                <legend>Engine and drive</legend>
              <label>Engine make<input {...guided(fleetFieldHelp.engineMake)} required value={form.engineMake} onChange={(event) => setForm({ ...form, engineMake: event.target.value })} /></label>
              <label>Engine model<input {...guided(fleetFieldHelp.engineModel)} required value={form.engineModel} onChange={(event) => setForm({ ...form, engineModel: event.target.value })} /></label>
              <label>Nameplate power (mechanical hp)<input {...guided(fleetFieldHelp.enginePower)} required type="number" min="0" step="any" value={form.enginePowerHp} onChange={(event) => setForm({ ...form, enginePowerHp: event.target.value })} /></label>
              {editingUnit === null ? null : (
                <p className="fleet-unit-note">
                  Existing derate and locations stay unchanged.
                </p>
              )}
              <details className="fleet-form__details">
                <summary>More engine details</summary>
                <label>Engine serial<input {...guided(fleetFieldHelp.engineSerial)} value={form.engineSerial} onChange={(event) => setForm({ ...form, engineSerial: event.target.value })} /></label>
              </details>
              </fieldset>
              <fieldset className="fleet-form__section">
                <legend>Speed limits</legend>
              <label>Engine RPM range<span className="fleet-range"><input {...guided(fleetFieldHelp.engineMinRpm)} aria-label="Minimum engine RPM" required type="number" min="1" value={form.engineMinRpm} onChange={(event) => setForm((current) => {
                const next = { ...current, engineMinRpm: event.target.value };
                return current.pumpRpmManual ? next : withCalculatedPumpRpm(next);
              })} /><input {...guided(fleetFieldHelp.engineMaxRpm)} aria-label="Maximum engine RPM" required type="number" min="1" value={form.engineMaxRpm} onChange={(event) => setForm((current) => {
                const next = { ...current, engineMaxRpm: event.target.value };
                return current.pumpRpmManual ? next : withCalculatedPumpRpm(next);
              })} /></span></label>
              <details className="fleet-form__details">
                <summary>Advanced drive limits</summary>
                <label>Gear ratio<input {...guided(fleetFieldHelp.gearRatio)} required type="number" min="0" step="any" value={form.gearRatio} onChange={(event) => setForm((current) => {
                  const next = { ...current, gearRatio: event.target.value };
                  return current.pumpRpmManual ? next : withCalculatedPumpRpm(next);
                })} /></label>
                <p id="pump-rpm-help" className="fleet-unit-note">Pump RPM = engine RPM × gear ratio, rounded to a whole RPM.</p>
                <label className="fleet-override-toggle"><input {...guided(fleetFieldHelp.manualPumpRpm)} type="checkbox" checked={form.pumpRpmManual} onChange={(event) => setForm((current) => event.target.checked ? { ...current, pumpRpmManual: true } : withCalculatedPumpRpm(current))} />Override pump RPM</label>
                <label>Pump RPM range<span className="fleet-range"><input {...guided(fleetFieldHelp.pumpMinRpm)} aria-describedby="pump-rpm-help" aria-label="Minimum pump RPM" required type="number" min="1" value={form.pumpMinRpm} onChange={(event) => setForm({ ...form, pumpMinRpm: event.target.value, pumpRpmManual: true })} /><input {...guided(fleetFieldHelp.pumpMaxRpm)} aria-describedby="pump-rpm-help" aria-label="Maximum pump RPM" required type="number" min="1" value={form.pumpMaxRpm} onChange={(event) => setForm({ ...form, pumpMaxRpm: event.target.value, pumpRpmManual: true })} /></span></label>
                <label>Speed increment (RPM)<input {...guided(fleetFieldHelp.speedIncrement)} required type="number" min="1" value={form.speedIncrementRpm} onChange={(event) => setForm({ ...form, speedIncrementRpm: event.target.value })} /></label>
              </details>
              </fieldset>
              <button type="submit" disabled={saveUnit.isPending || templates.isLoading || !form.templateId || !form.revisionId || !form.variantId}>{editingId === null ? 'Add unit' : 'Save unit'}</button>
            </form>
              {saveUnit.isError ? (
                <p role="alert">
                  {saveUnit.error instanceof Error
                    ? saveUnit.error.message
                    : 'Pump unit could not be saved.'}
                </p>
              ) : null}
          </Card>
          <details className="card fleet-disclosure">
            <summary>Import fleet CSV</summary>
            <div className="fleet-disclosure__body">
              <label>CSV content<textarea {...guided(fleetFieldHelp.csv)} rows={5} value={csvText} onChange={(event) => setCsvText(event.target.value)} /></label>
              <button type="button" disabled={!csvText || importUnits.isPending} onClick={() => importUnits.mutate()}>Import CSV</button>
            </div>
          </details>
        </div>
        <Card title={`Saved units (${units.data?.total ?? 0})`}>
          {units.isLoading ? <p>Loading units…</p> : null}
          <ul className="fleet-list">
            {units.data?.items.map((unit) => (
              <li key={unit.id}>
                <div><strong>{unit.physical_identifier} · {unit.name}</strong><span>{unit.template_name} · {unit.manufacturer} {unit.model}</span><span>{unit.optimization_eligible ? 'Optimization ready' : 'Not optimization ready'}</span></div>
                <div><button type="button" onClick={() => void beginEdit(unit.id)}>Edit</button><button type="button" onClick={() => setArchive.mutate({ archived: unit.archived, id: unit.id })}>{unit.archived ? 'Restore' : 'Archive'}</button></div>
              </li>
            ))}
          </ul>
        </Card>
        </div>
      </div>
    </Page>
  );
}
